import { AfterViewInit, ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { Mapping } from 'src/app/services/dtos/mapping';
import { Rule } from 'src/app/services/dtos/rule';
import { RightsService } from 'src/app/services/rights.service';
import { CustomersState } from 'src/app/states/customers-state/customers.state';
import { ClearActivePluginId, ClearCurrentRuleId, DeleteRule, SetActivePluginId, SetCurrentRuleId, SetCustomerMappingIds }
  from 'src/app/states/mapping-state/mapping.actions';
import { MappingState } from 'src/app/states/mapping-state/mapping.state';
import { RuleDialogComponent } from '../rule-dialog/rule-dialog.component';

@Component({
  selector: 'app-mapping-rule-configuration',
  templateUrl: './mapping-rule-configuration.component.html',
  styleUrls: ['./mapping-rule-configuration.component.scss']
})
export class MappingRuleConfigurationComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatSort) public sort!: MatSort;

  public customerMappings$: Observable<string[]> = inject(Store).select(CustomersState.getSelectedCustomerMappings);
  public activePluginId$: Observable<string> = inject(Store).select(CustomersState.getActivePluginId);
  public mappings$: Observable<Mapping[]> = inject(Store).select(MappingState.getAll);
  public selectedCustomerId$: Observable<string> = inject(Store).select(CustomersState.getSelectedCustomerId);
  
  @ViewChild(MatPaginator) private paginator!: MatPaginator;

  @Input() private isInCustomerConfigurationMode!: boolean;

  public customerMappings!: string[];
  public activePluginId!: string;
  public mappings!: Mapping[];
  public selectedCustomerId!: string;
  public dataSource!: MatTableDataSource<Rule>;
  public displayedColumns: string[] = ['mappingName', 'targetPath', 'edit', 'delete'];

  private subs = new Array<Subscription>();

  public constructor(
    public accessService: RightsService,
    private store: Store,
    private dialogService: MatDialog,
    private cd: ChangeDetectorRef
  ) { }

  public ngOnDestroy(): void {
    this.subs.forEach(x => x.unsubscribe());
  }

  public ngOnInit(): void {
    this.dataSource = new MatTableDataSource<Rule>();
    if (this.isInCustomerConfigurationMode){
      this.subs.push(this.selectedCustomerId$.subscribe(selectedCustomerId => {
        if (selectedCustomerId) {
          this.selectedCustomerId = selectedCustomerId;
        }
      }));

      this.subs.push(this.activePluginId$.subscribe(activePluginId => {
        if (activePluginId) {
          this.activePluginId = activePluginId;
          this.reloadRulesDataSource();
        }
      }));
      this.subs.push(this.mappings$.subscribe(
      mappings => {
        this.mappings = mappings;
        this.subs.push(this.customerMappings$.subscribe((customerMappings) => {
          if (customerMappings){
            this.customerMappings = customerMappings;
            this.reloadRulesDataSource();
          }
        }));
      }
    ));
    }
  }

  public ngAfterViewInit(): void{
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.sort.sort(({ id: 'mappingName', start: 'asc' }) as MatSortable);
    this.cd.detectChanges();
  }

  public openAddDialog(): void {
    this.openRuleDialog();
  }

  public openEditDialog(ruleId: string): void {
    this.store.dispatch(new SetCurrentRuleId(ruleId));
    this.openRuleDialog();
  }

  public removeRule(ruleId: string): void{
    const shouldDelete = confirm('Are you sure you want to remove this rule?');
    if (shouldDelete) {
      this.store.dispatch(new DeleteRule(ruleId, this.selectedCustomerId));
    }
  }

  private openRuleDialog(): void {
    this.setActivePluginId();
    this.setCustomerMappingIds();
    const dialog = this.dialogService.open(RuleDialogComponent, {
      minWidth: '50vw',
      maxWidth: '50vw',
      restoreFocus: false,
      autoFocus: false,
  });

    dialog.componentInstance.selectedCustomerId = this.selectedCustomerId;
    this.clearCurrentRuleId(dialog);
  }

  private clearCurrentRuleId(dialog: MatDialogRef<RuleDialogComponent>): void {
    this.subs.push(
      dialog.afterClosed().subscribe(() => {
        this.store.dispatch(new ClearCurrentRuleId());
        this.store.dispatch(new ClearActivePluginId());
      })
    );
  }

  private setActivePluginId(): void {
    this.store.dispatch(new SetActivePluginId(this.activePluginId));
  }

  private setCustomerMappingIds(): void {
    this.store.dispatch(new SetCustomerMappingIds(this.customerMappings));
  }

  private reloadRulesDataSource(): void {
    const data = this.mappings?.filter(x => this.customerMappings?.includes(x.id))
      .map(m => m.rules?.map(r => ({ ...r, mappingName: m.name }))).reduce((pre, cur) => pre.concat(cur), []);

    const dataFiltered = data?.filter(r => r.pluginId === this.activePluginId);
    this.dataSource.data = dataFiltered || [];
  }
}
